import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useMenuVisibility, type MenuItemKey } from '../../hooks/useMenuVisibility';
import { SITE_LOGO } from '../../constants/assets';

const ALL_NAV_LINKS = [
  { label: 'Alta Costura', path: '/alta-costura', key: 'alta-costura' as MenuItemKey },
  { label: 'Asesoría', path: '/asesoria-imagen', key: 'asesoria-imagen' as MenuItemKey },
  { label: 'Vestidos de Novia', path: '/vestidos-novia', key: 'vestidos-novia' as MenuItemKey },
  { label: 'Trajes a Medida', path: '/trajes-medida', key: 'trajes-medida' as MenuItemKey },
  { label: 'Tienda', path: '/tienda', key: 'tienda' as MenuItemKey },
  { label: 'Blog', path: '/blog', key: 'blog' as MenuItemKey },
  { label: 'Arreglos', path: '/pret-a-porter', key: 'pret-a-porter' as MenuItemKey },
  { label: 'Contacto', path: '/contacto', key: 'contacto' as MenuItemKey },
];

const ALL_MENU_GROUPS = [
  {
    title: 'Servicios',
    links: [
      { label: 'Alta Costura', path: '/alta-costura', key: 'alta-costura' as MenuItemKey },
      { label: 'Asesoría de Imagen', path: '/asesoria-imagen', key: 'asesoria-imagen' as MenuItemKey },
      { label: 'Vestidos de Novia', path: '/vestidos-novia', key: 'vestidos-novia' as MenuItemKey },
      { label: 'Trajes a Medida', path: '/trajes-medida', key: 'trajes-medida' as MenuItemKey },
      { label: 'Prêt-à-porter', path: '/pret-a-porter', key: 'pret-a-porter' as MenuItemKey },
      { label: 'Tienda', path: '/tienda', key: 'tienda' as MenuItemKey },
    ],
  },
  {
    title: 'Compañía',
    links: [
      { label: 'Sobre Nosotros', path: '/sobre-nosotros', key: 'sobre-nosotros' as MenuItemKey },
      { label: 'Blog', path: '/blog', key: 'blog' as MenuItemKey },
      { label: 'Testimonios', path: '/testimonios', key: 'testimonios' as MenuItemKey },
      { label: 'Contacto', path: '/contacto', key: 'contacto' as MenuItemKey },
    ],
  },
  {
    title: 'Reservas',
    links: [
      { label: 'Mi Reserva', path: '/mi-reserva', key: 'mi-reserva' as MenuItemKey },
      { label: 'Cita Privada', path: '/alta-costura', key: 'alta-costura' as MenuItemKey },
      { label: 'Arreglos', path: '/pret-a-porter', key: 'pret-a-porter' as MenuItemKey },
      { label: 'Experiencia', path: '/experiencia', key: 'experiencia' as MenuItemKey },
    ],
  },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { totalItems, setIsCartOpen } = useCart();
  const { state: visibility, loading } = useMenuVisibility();

  const navLinks = useMemo(
    () => ALL_NAV_LINKS.filter((link) => visibility.header[link.key] !== false),
    [visibility.header]
  );

  const menuGroups = useMemo(
    () =>
      ALL_MENU_GROUPS.map((group) => ({
        ...group,
        links: group.links.filter((link) => visibility.hamburger[link.key] !== false),
      })).filter((group) => group.links.length > 0),
    [visibility.hamburger]
  );

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const isHome = location.pathname === '/';

  // Don't render complex logic until visibility is loaded (or just use default)
  // To avoid flicker, we use the initial DEFAULT_STATE from hook

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled || !isHome ? 'px-3 sm:px-5 lg:px-8 pt-3' : ''
        }`}
      >
        <div
          className={`flex items-center justify-between h-24 lg:h-28 transition-all duration-500 ${
            scrolled || !isHome
              ? 'w-full bg-white/90 backdrop-blur-md rounded-[32px] border border-brand-gray-200/50 px-6 lg:px-10'
              : 'w-full px-6 lg:px-[7%]'
          }`}
        >
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img
              src={SITE_LOGO}
              alt="Anthony Sánchez Couture"
              className="h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-10 xl:gap-14">
            {!loading && navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.path}
                className={`font-sans text-[13px] tracking-widest uppercase transition-colors duration-300 whitespace-nowrap ${
                  scrolled || !isHome ? 'text-brand-black hover:text-brand-gray-600' : 'text-white hover:text-white/70'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-3 lg:gap-4">
            {/* Cart Icon */}
            {!loading && visibility.header['tienda'] !== false && (
              <button
                onClick={() => setIsCartOpen(true)}
                className={`relative transition-colors duration-300 ${
                  scrolled || !isHome ? 'text-brand-black hover:text-brand-gray-600' : 'text-white hover:text-white/70'
                }`}
                title="Carrito"
              >
                <div className="w-9 h-9 flex items-center justify-center">
                  <i className="ri-shopping-bag-line text-lg" />
                </div>
                {totalItems > 0 && (
                  <span className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-brand-sage text-brand-black text-[9px] font-bold flex items-center justify-center">
                    {totalItems}
                  </span>
                )}
              </button>
            )}

            {/* Hamburger — visible on all breakpoints */}
            <button
              onClick={() => setMenuOpen(true)}
              className={`w-10 h-10 flex flex-col items-center justify-center gap-1.5 transition-colors ${
                scrolled || !isHome ? 'text-brand-black' : 'text-white'
              }`}
              aria-label="Menú"
            >
              <span className={`block w-5 h-px transition-all duration-300 ${scrolled || !isHome ? 'bg-brand-black' : 'bg-white'}`} />
              <span className={`block w-5 h-px transition-all duration-300 ${scrolled || !isHome ? 'bg-brand-black' : 'bg-white'}`} />
            </button>
          </div>
        </div>
      </nav>

      {/* Full-screen menu overlay */}
      <div
        className={`fixed inset-0 z-[60] transition-all duration-500 ${
          menuOpen ? 'pointer-events-auto' : 'pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-brand-black/40 backdrop-blur-sm transition-opacity duration-500 ${
            menuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setMenuOpen(false)}
        />

        {/* Panel */}
        <div
          className={`absolute top-3 right-3 sm:right-5 lg:right-8 bottom-3 sm:bottom-5 lg:bottom-8 w-full max-w-sm sm:max-w-md bg-white rounded-[32px] shadow-2xl flex flex-col transition-all duration-500 ${
            menuOpen ? 'translate-x-0 opacity-100' : 'translate-x-8 opacity-0'
          }`}
        >
          {/* Panel Header */}
          <div className="flex items-center justify-between px-8 pt-8 pb-4">
            <span className="font-sans text-[11px] tracking-[0.2em] uppercase text-brand-gray-400">
              Menú
            </span>
            <button
              onClick={() => setMenuOpen(false)}
              className="w-10 h-10 rounded-full bg-brand-gray-100 flex items-center justify-center text-brand-black hover:bg-brand-gray-200 transition-colors"
              aria-label="Cerrar menú"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>

          {/* Panel Content */}
          <div className="flex-1 overflow-y-auto px-8 pb-8">
            <div className="grid grid-cols-1 gap-8">
              {!loading && menuGroups.map((group) => (
                <div key={group.title}>
                  <h4 className="font-sans text-[11px] tracking-[0.2em] uppercase text-brand-gray-400 font-medium mb-4">
                    {group.title}
                  </h4>
                  <ul className="space-y-3">
                    {group.links.map((link) => (
                      <li key={link.label}>
                        <Link
                          to={link.path}
                          onClick={() => setMenuOpen(false)}
                          className="font-sans text-brand-black text-[15px] hover:text-brand-gray-500 transition-colors duration-200 inline-block"
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Contact mini-card */}
            <div className="mt-10 p-5 rounded-2xl bg-brand-gray-50">
              <h4 className="font-sans text-[11px] tracking-[0.2em] uppercase text-brand-gray-400 font-medium mb-3">
                Atelier
              </h4>
              <p className="font-sans text-brand-gray-600 text-sm leading-relaxed">
                Rambla de Catalunya, 85
                <br />
                Eixample, Barcelona
              </p>
              <a
                href="mailto:anthosanchez3108@gmail.com"
                className="font-sans text-brand-black text-sm mt-2 inline-block hover:text-brand-gray-500 transition-colors"
              >
                anthosanchez3108@gmail.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}